// Client for the Insights chat API, served by the Django backend
// (grape_backend/app/outfit_insights) under /insights/ on REACT_APP_BASE_URL.
// The chat endpoint streams Server-Sent Events over a POST, so it uses fetch
// rather than axios / EventSource.

const baseURL = `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}/insights`;

export class InsightsApiError extends Error {
	constructor(message, { status, code } = {}) {
		super(message);
		this.status = status;
		this.code = code;
	}
}

function authHeaders() {
	const token = localStorage.getItem("token");
	return token ? { Authorization: `Bearer ${token}` } : {};
}

async function raiseForStatus(response) {
	if (response.ok) {
		return;
	}
	let body = {};
	try {
		body = await response.json();
	} catch {
		// Non-JSON error body
	}
	if (response.status === 401) {
		// Same behaviour as the axios interceptor: the session is gone.
		localStorage.clear();
		window.location.href = "/auth/login";
	}
	throw new InsightsApiError(body.error || body.detail || `Request failed (${response.status})`, {
		status: response.status,
		code: body.code,
	});
}

export async function getInsightsConfig() {
	const response = await fetch(`${baseURL}/config/`, { headers: authHeaders() });
	await raiseForStatus(response);
	return response.json();
}

export async function getTopInsightsQueries(limit = 20) {
	const response = await fetch(`${baseURL}/top-queries/?limit=${limit}`, { headers: authHeaders() });
	await raiseForStatus(response);
	return (await response.json()).queries;
}

export async function deleteInsightsConversation(conversationId) {
	await fetch(`${baseURL}/conversations/${encodeURIComponent(conversationId)}/`, {
		method: "DELETE",
		headers: authHeaders(),
	}).catch(() => { });
}

/**
 * Sends one question and calls onEvent(eventName, data) for every streamed
 * event until the answer is complete. Resolves when the stream ends.
 */
export async function streamInsightsChat({ message, conversationId, database, signal, onEvent }) {
	const response = await fetch(`${baseURL}/chat/`, {
		method: "POST",
		headers: { "Content-Type": "application/json", Accept: "text/event-stream", ...authHeaders() },
		body: JSON.stringify({ message, conversation_id: conversationId || undefined, database }),
		signal,
	});
	await raiseForStatus(response);

	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";

	const dispatch = (chunk) => {
		let event = "message";
		const data = [];
		for (const line of chunk.split("\n")) {
			if (line.startsWith("event:")) {
				event = line.slice(6).trim();
			} else if (line.startsWith("data:")) {
				data.push(line.slice(5).trimStart());
			}
		}
		if (!data.length) { // keep-alive comment
			return;
		}
		try {
			onEvent(event, JSON.parse(data.join("\n")));
		} catch (error) {
			console.error("Could not parse insights event", error);
		}
	};

	for (; ;) {
		const { value, done } = await reader.read();
		if (done) {
			break;
		}
		buffer += decoder.decode(value, { stream: true });
		let boundary = buffer.indexOf("\n\n");
		while (boundary !== -1) {
			dispatch(buffer.slice(0, boundary));
			buffer = buffer.slice(boundary + 2);
			boundary = buffer.indexOf("\n\n");
		}
	}
	if (buffer.trim()) {
		dispatch(buffer);
	}
}
